import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Audio, AVPlaybackStatus, InterruptionModeAndroid, InterruptionModeIOS } from 'expo-av';
import { Platform } from 'react-native';
import * as Haptics from 'expo-haptics';
import { prefetchAudio } from '@/services/audioPrefetchService';

interface AudioTrack {
  stepNumber: number;
  stepTitle: string;
  audioUrl: string;
}

interface AudioPlayerContextType {
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  isLoading: boolean;
  position: number;
  duration: number;
  playTrack: (track: AudioTrack) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  stop: () => Promise<void>;
  seekTo: (position: number) => Promise<void>;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  const soundRef = useRef<Audio.Sound | null>(null);
  const statusPollingInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  // Monotonic counter for playTrack calls. Each call captures its own ID and
  // bails if a newer call has superseded it — prevents rapid-tap races where
  // the prefetch + createAsync await window lets two playTrack calls
  // interleave and leak a stray Sound. Only the latest tap "wins."
  const playRequestIdRef = useRef(0);

  useEffect(() => {
    configureAudioMode();
    
    return () => {
      if (soundRef.current) {
        soundRef.current.unloadAsync();
      }
      if (statusPollingInterval.current) {
        clearInterval(statusPollingInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (Platform.OS === 'android' && soundRef.current) {
      if (statusPollingInterval.current) {
        clearInterval(statusPollingInterval.current);
      }
      
      statusPollingInterval.current = setInterval(async () => {
        if (soundRef.current) {
          try {
            const status = await soundRef.current.getStatusAsync();
            if (status.isLoaded) {
              setIsPlaying(status.isPlaying);
              setPosition(status.positionMillis);
              setDuration(status.durationMillis || 0);
            }
          } catch (error) {
            console.error('❌ Error polling audio status:', error);
          }
        }
      }, 500);
      
      return () => {
        if (statusPollingInterval.current) {
          clearInterval(statusPollingInterval.current);
        }
      };
    }
    return undefined;
  }, [soundRef.current]);

  const configureAudioMode = async () => {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        staysActiveInBackground: true,
        playsInSilentModeIOS: true,
        shouldDuckAndroid: true,
        playThroughEarpieceAndroid: false,
        interruptionModeAndroid: InterruptionModeAndroid.DuckOthers,
        interruptionModeIOS: InterruptionModeIOS.DuckOthers,
      });
      console.log('✅ Audio mode configured for background playback');
    } catch (error) {
      console.error('❌ Error configuring audio mode:', error);
    }
  };

  const onPlaybackStatusUpdate = (status: AVPlaybackStatus) => {
    if (!status.isLoaded) {
      if (status.error) {
        console.error('❌ Audio playback error:', status.error);
      }
      return;
    }

    setIsPlaying(status.isPlaying);
    setPosition(status.positionMillis);
    setDuration(status.durationMillis || 0);

    if (status.didJustFinish) {
      setIsPlaying(false);
      setPosition(0);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
  };

  const playTrack = async (track: AudioTrack) => {
    // Capture a request ID up-front. Every async checkpoint below verifies
    // we're still the latest call before mutating shared state — prevents
    // rapid-tap races where two playTrack calls interleave their prefetch +
    // createAsync awaits and leak a stray Sound.
    const requestId = ++playRequestIdRef.current;

    try {
      setIsLoading(true);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

      if (soundRef.current) {
        await soundRef.current.unloadAsync();
        soundRef.current = null;
      }

      if (requestId !== playRequestIdRef.current) {
        console.log('🎵 playTrack superseded after unload, aborting');
        return;
      }

      // Resolve to local cached file when possible. If the file is already on
      // disk, returns instantly (zero network). If a prefetch is in flight,
      // awaits the same promise (no double download). If neither, downloads
      // now. On any failure we fall back to streaming the remote URL so
      // playback still works — never block the user behind a cache miss.
      let uri = track.audioUrl;
      try {
        uri = await prefetchAudio(track.audioUrl);
      } catch (prefetchErr) {
        console.warn('⚠️ Audio prefetch failed, streaming remote URL:', prefetchErr);
        uri = track.audioUrl;
      }

      if (requestId !== playRequestIdRef.current) {
        console.log('🎵 playTrack superseded after prefetch, aborting');
        return;
      }

      console.log('🎵 Loading audio:', uri);

      const { sound } = await Audio.Sound.createAsync(
        { uri },
        { shouldPlay: true },
        onPlaybackStatusUpdate
      );

      // If a newer call superseded us during createAsync, unload the orphan
      // Sound we just created (otherwise it leaks native audio session
      // resources) and bail before clobbering soundRef.
      if (requestId !== playRequestIdRef.current) {
        console.log('🎵 playTrack superseded after createAsync, unloading orphan');
        await sound.unloadAsync().catch(() => {});
        return;
      }

      soundRef.current = sound;
      setCurrentTrack(track);
      setIsPlaying(true);

      console.log('✅ Audio loaded and playing');
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      // Only the winning call clears the loading state — losers leave it
      // alone so the winner's spinner stays accurate.
      if (requestId === playRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const pause = async () => {
    try {
      if (soundRef.current) {
        const status = await soundRef.current.getStatusAsync();
        if (status.isLoaded && status.isPlaying) {
          await soundRef.current.pauseAsync();
          setIsPlaying(false);
          console.log('⏸️ Audio paused');
        }
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    } catch (error) {
      console.error('❌ Error pausing audio:', error);
    }
  };

  const resume = async () => {
    try {
      if (soundRef.current) {
        await configureAudioMode();
        
        const status = await soundRef.current.getStatusAsync();
        if (status.isLoaded && !status.isPlaying) {
          await soundRef.current.playAsync();
          setIsPlaying(true);
          console.log('▶️ Audio resumed');
        }
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    } catch (error) {
      console.error('❌ Error resuming audio:', error);
    }
  };

  const stop = async () => {
    try {
      if (soundRef.current) {
        await soundRef.current.stopAsync();
        await soundRef.current.unloadAsync();
        soundRef.current = null;
      }
      setIsPlaying(false);
      setPosition(0);
      setDuration(0);
      setCurrentTrack(null);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    } catch (error) {
      console.error('❌ Error stopping audio:', error);
    }
  };

  const seekTo = async (positionMs: number) => {
    try {
      if (soundRef.current) {
        const status = await soundRef.current.getStatusAsync();
        if (status.isLoaded) {
          await soundRef.current.setPositionAsync(positionMs);
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('seeking interrupted')) {
        return;
      }
      console.error('❌ Error seeking audio:', error);
    }
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        isLoading,
        position,
        duration,
        playTrack,
        pause,
        resume,
        stop,
        seekTo,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
}
